import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import { PageHeader } from '../shared/components/PageHeader';
import { Card, CardHeader } from '../shared/components/Card';
import { Button } from '../shared/components/Button';
import { Alert, EmptyState, Spinner } from '../shared/components/feedback';
import { Avatar } from '../shared/components/Avatar';
import { LineChart } from '../shared/components/charts';
import { RefreshIcon } from '../shared/components/icons';
import { useActiveAccount } from '../account/ActiveAccountContext';
import * as threadPostApi from '../api/threadPost';
import { ApiError } from '../api/client';
import type {
  DailyPostStat,
  ThreadAccountPostResponse,
  ThreadPostStatisticsResponse,
} from '../api/types';
import { contentRecommendations, summarizeInsights } from './postInsights';
import { formatWallClockKo } from '../shared/datetimeWallClock';
import { AiUsagePanel } from './AiUsagePanel';

type RangeDays = 7 | 14 | 30;

export default function DashboardPage() {
  const theme = useTheme();
  const { selectedAccount, selectedAccountId } = useActiveAccount();
  const [insightRefresh, setInsightRefresh] = useState(0);
  const [insightError, setInsightError] = useState<string | null>(null);
  const statsRequest = useRef(0);

  const [range, setRange] = useState<RangeDays>(14);
  const [stats, setStats] = useState<ThreadPostStatisticsResponse | null>(null);
  const [daily, setDaily] = useState<DailyPostStat[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [accountPosts, setAccountPosts] = useState<
    ThreadAccountPostResponse[]
  >([]);
  const [postsLoading, setPostsLoading] = useState(false);

  const fetchStats = useCallback(async () => {
    const id = ++statsRequest.current;
    setLoading(true);
    setError(null);
    try {
      const [statsData, dailyData] = await Promise.all([
        threadPostApi.getStatistics(),
        threadPostApi.getDailyStatistics(range),
      ]);
      if (id !== statsRequest.current) return;
      setStats(statsData);
      setDaily(dailyData);
    } catch (err) {
      if (id !== statsRequest.current) return;
      setError(
        err instanceof ApiError ? err.message : '통계를 불러오지 못했습니다.'
      );
    } finally {
      if (id === statsRequest.current) setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    void fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    let cancelled = false;
    setAccountPosts([]);
    setInsightError(null);
    if (selectedAccountId == null) {
      setPostsLoading(false);
      return;
    }
    setPostsLoading(true);
    threadPostApi.getPostsByThreadAccount(selectedAccountId, { includeInsights: true })
      .then((posts) => { if (!cancelled) setAccountPosts(posts); })
      .catch((err) => { if (!cancelled) setInsightError(err instanceof Error ? err.message : '인사이트를 불러오지 못했습니다.'); })
      .finally(() => { if (!cancelled) setPostsLoading(false); });
    return () => { cancelled = true; };
  }, [selectedAccountId, insightRefresh]);

  const reservedCount = stats?.pendingReservationCount ?? 0;
  const progressCount = stats?.processingReservationCount ?? 0;
  const registeredCount = stats?.registeredCount ?? 0;

  const trend = useMemo(() => {
    const labels = daily.map((d) => {
      const [, m, day] = d.date.split('-');
      return `${Number(m)}/${Number(day)}`;
    });
    return {
      labels,
      series: [
        {
          name: '예약',
          color: theme.colors.primary,
          values: daily.map((d) => d.reservationCount ?? 0),
        },
        {
          name: '발행',
          color: theme.colors.info,
          values: daily.map((d) => d.registeredCount),
        },
        {
          name: '해제',
          color: theme.colors.danger,
          values: daily.map((d) => d.unregisteredCount),
        },
      ],
    };
  }, [daily, theme.colors]);

  const hasTrendData = useMemo(
    () => trend.series.some((s) => s.values.some((v) => v > 0)),
    [trend]
  );

  const publishSpark = useMemo(
    () => daily.map((d) => d.registeredCount),
    [daily]
  );
  const unregSpark = useMemo(
    () => daily.map((d) => d.unregisteredCount),
    [daily]
  );
  const reservedSpark = useMemo(
    () => daily.map((d) => d.reservationCount ?? 0),
    [daily]
  );

  const insightSeries = useMemo(
    () => summarizeInsights(accountPosts, range),
    [accountPosts, range]
  );
  const recommendations = useMemo(() => contentRecommendations(accountPosts, range), [accountPosts, range]);
  const latestInsightSample = accountPosts.map((post) => post.insights?.sampledAt).filter((value): value is string => !!value).sort().slice(-1)[0];

  const rankedAccounts = useMemo(() => {
    if (!stats?.accounts.length) return { strong: [], weak: [], board: [] };
    const withRatio = stats.accounts.map((a) => {
      const total = Math.max(1, a.totalCount);
      return {
        ...a,
        unregRatio: a.unregisteredCount / total,
      };
    });
    const strong = [...withRatio]
      .sort((a, b) => b.registeredCount - a.registeredCount)
      .slice(0, 5);
    const weak = [...withRatio]
      .filter((a) => a.unregisteredCount > 0)
      .sort((a, b) => b.unregisteredCount - a.unregisteredCount)
      .slice(0, 5);
    const board = [...withRatio].sort(
      (a, b) => b.registeredCount - a.registeredCount
    );
    return { strong, weak, board };
  }, [stats]);

  const totalRegisteredAll = Math.max(
    1,
    rankedAccounts.strong.reduce((s, a) => s + a.registeredCount, 0)
  );
  const totalUnregAll = Math.max(
    1,
    rankedAccounts.weak.reduce((s, a) => s + a.unregisteredCount, 0)
  );

  return (
    <ReportPage>
      <PageHeader
        title="리포트"
        description="발행 현황부터 계정별 반응, AI 사용량까지 한눈에 확인하세요."
        action={
          <HeaderActions>
            <Button
              $variant="secondary"
              $size="sm"
              disabled={loading || postsLoading}
              onClick={() => { void fetchStats(); setInsightRefresh((n) => n + 1); }}
            >
              <RefreshIcon size={15} /> 새로고침
            </Button>
            <Button as={Link} to="/board" $size="sm">콘텐츠 보드</Button>
          </HeaderActions>
        }
      />

      <FilterRow>
        <FilterPill>
          조회 기간
          <select
            aria-label="추이·인사이트 조회 기간"
            value={range}
            onChange={(e) => setRange(Number(e.target.value) as RangeDays)}
          >
            <option value={7}>최근 7일</option>
            <option value={14}>최근 14일</option>
            <option value={30}>최근 30일</option>
          </select>
        </FilterPill>
        <FilterPill $static>
          운영 현황 <strong>전체 계정 · 누적</strong>
        </FilterPill>
      </FilterRow>

      {error && (
        <AlertWrap>
          <Alert tone="danger">{error}</Alert>
        </AlertWrap>
      )}

      <MetricGrid>
        <MetricCard>
          <MetricLabel>예약 작업</MetricLabel>
          <MetricValue>
            {loading ? <Spinner $size={20} /> : stats ? reservedCount.toLocaleString('ko-KR') : '–'}
            <MetricSlash>/ 진행 {loading || !stats ? '–' : progressCount}</MetricSlash>
          </MetricValue>
          <Sparkline values={reservedSpark} color={theme.colors.primary} />
        </MetricCard>
        <MetricCard>
          <MetricLabel>발행 완료</MetricLabel>
          <MetricValue>
            {loading ? (
              <Spinner $size={20} />
            ) : (
              stats ? registeredCount.toLocaleString('ko-KR') : '–'
            )}
          </MetricValue>
          <Sparkline values={publishSpark} color={theme.colors.info} />
        </MetricCard>
        <MetricCard>
          <MetricLabel>등록 해제</MetricLabel>
          <MetricValue>
            {loading ? (
              <Spinner $size={20} />
            ) : (
              stats ? stats.unregisteredCount.toLocaleString('ko-KR') : '–'
            )}
          </MetricValue>
          <Sparkline values={unregSpark} color={theme.colors.danger} />
        </MetricCard>
      </MetricGrid>

      <SectionGap />

      <Card>
        <CardHeader
          title="콘텐츠 추이"
          description={`최근 ${range}일 · 예약 / 발행 / 해제`}
        />
        {loading ? (
          <Center>
            <Spinner $size={24} />
          </Center>
        ) : !hasTrendData ? (
          <EmptyState
            title="표시할 추이 데이터가 없습니다"
            description="예약하거나 게시물을 발행하면 일별 그래프가 나타납니다."
          />
        ) : (
          <LineChart label="콘텐츠 예약·발행·해제 추이" labels={trend.labels} series={trend.series} />
        )}
      </Card>

      <SectionGap />

      <SectionHeading>선택 계정 성과</SectionHeading>
      <ScopeNote>
        {selectedAccountId == null ? '상단에서 계정을 선택하면 성과를 확인할 수 있습니다.' : `${selectedAccount?.threadAccountProfileResponse?.threadUserName ? '@' + selectedAccount.threadAccountProfileResponse.threadUserName : '계정 #' + selectedAccountId} · 최근 ${range}일 발행한 글 ${insightSeries.avg?.count ?? 0}개의 현재 누적 반응 · 한국 시간 기준`}
      </ScopeNote>
      {insightError && <Alert tone="danger">{insightError}</Alert>}
      {latestInsightSample && <ScopeNote>최근 업데이트 {formatWallClockKo(latestInsightSample)}</ScopeNote>}
      <InsightGrid>
        <InsightCard>
          <InsightLabel>평균 조회수</InsightLabel>
          <InsightValue>
            {postsLoading ? (
              <Spinner $size={18} />
            ) : insightSeries.avg ? (
              insightSeries.avg.avgViews?.toLocaleString('ko-KR') ?? '–'
            ) : (
              '–'
            )}
          </InsightValue>
          <Sparkline
            values={insightSeries.views}
            color={theme.colors.info}
          />
        </InsightCard>
        <InsightCard>
          <InsightLabel>평균 좋아요</InsightLabel>
          <InsightValue>
            {postsLoading ? (
              <Spinner $size={18} />
            ) : insightSeries.avg ? (
              insightSeries.avg.avgLikes?.toLocaleString('ko-KR') ?? '–'
            ) : (
              '–'
            )}
          </InsightValue>
          <Sparkline values={insightSeries.likes} color={theme.colors.success} />
        </InsightCard>
        <InsightCard>
          <InsightLabel>평균 댓글</InsightLabel>
          <InsightValue>
            {postsLoading ? (
              <Spinner $size={18} />
            ) : insightSeries.avg ? (
              insightSeries.avg.avgReplies?.toLocaleString('ko-KR') ?? '–'
            ) : (
              '–'
            )}
          </InsightValue>
          <Sparkline
            values={insightSeries.replies}
            color={theme.colors.primary}
          />
        </InsightCard>
      </InsightGrid>

      <SectionGap />
      <Card>
        <CardHeader title="다음 콘텐츠 참고" description="선택 계정에서 반응이 좋았던 주제와 발행 시간을 확인하세요." />
        {recommendations.category || recommendations.time ? (
          <div style={{ fontSize: 13, lineHeight: 1.8 }}>
            {recommendations.category && <p>반응률이 높은 분류: <strong>{recommendations.category.name}</strong> · {recommendations.category.count}개 글 · {recommendations.category.engagementRate.toFixed(2)}%</p>}
            {recommendations.time && <p>반응률이 높은 발행 시간: <strong>{recommendations.time.name}</strong> · {recommendations.time.count}개 글 · {recommendations.time.engagementRate.toFixed(2)}%</p>}
            <p>반응률은 좋아요·댓글 합계를 조회수로 나눈 값입니다. 글마다 누적 관측 기간이 달라 다음 실험의 참고 자료로 사용하세요.</p>
          </div>
        ) : <EmptyState title="비교할 성과가 더 필요합니다" description="최근 선택 기간에 조회·좋아요·댓글이 확인된 글이 분류 또는 시간대별로 3개 이상, 비교 그룹이 2개 이상 모이면 표시합니다." />}
      </Card>

      <SectionGap />

      <SplitGrid>
        <Card>
          <CardHeader title="발행이 많은 계정" description="등록 게시물 수" />
          {loading ? (
            <Center>
              <Spinner $size={22} />
            </Center>
          ) : rankedAccounts.strong.length === 0 ? (
            <EmptyState title="집계할 계정이 없습니다" />
          ) : (
            <TopicList>
              {rankedAccounts.strong.map((acc) => {
                const share =
                  (acc.registeredCount / totalRegisteredAll) * 100;
                return (
                  <TopicRow key={acc.threadAccountId}>
                    <Avatar
                      src={acc.threadProfilePictureUrl}
                      name={acc.threadUserName}
                      size={36}
                    />
                    <TopicBody>
                      <TopicTop>
                        <TopicName>
                          {acc.threadUserName
                            ? `@${acc.threadUserName}`
                            : `계정 #${acc.threadAccountId}`}
                        </TopicName>
                        <TopicCount>{acc.registeredCount}건</TopicCount>
                      </TopicTop>
                      <ProgressTrack>
                        <ProgressFill
                          $tone="strong"
                          style={{ width: `${Math.max(share, 2)}%` }}
                        />
                      </ProgressTrack>
                    </TopicBody>
                  </TopicRow>
                );
              })}
            </TopicList>
          )}
        </Card>

        <Card>
          <CardHeader title="해제가 많은 계정" description="삭제·해제 게시물 수" />
          {loading ? (
            <Center>
              <Spinner $size={22} />
            </Center>
          ) : rankedAccounts.weak.length === 0 ? (
            <EmptyState title="해제된 게시물이 없습니다" />
          ) : (
            <TopicList>
              {rankedAccounts.weak.map((acc) => {
                const share = (acc.unregisteredCount / totalUnregAll) * 100;
                return (
                  <TopicRow key={acc.threadAccountId}>
                    <Avatar
                      src={acc.threadProfilePictureUrl}
                      name={acc.threadUserName}
                      size={36}
                    />
                    <TopicBody>
                      <TopicTop>
                        <TopicName>
                          {acc.threadUserName
                            ? `@${acc.threadUserName}`
                            : `계정 #${acc.threadAccountId}`}
                        </TopicName>
                        <TopicCount>{acc.unregisteredCount}건</TopicCount>
                      </TopicTop>
                      <ProgressTrack>
                        <ProgressFill
                          $tone="weak"
                          style={{ width: `${Math.max(share, 2)}%` }}
                        />
                      </ProgressTrack>
                    </TopicBody>
                  </TopicRow>
                );
              })}
            </TopicList>
          )}
        </Card>
      </SplitGrid>

      <SectionGap />

      <Card>
        <CardHeader
          title="계정 리더보드"
          description="발행 수 · 해제 수 · 합계"
          action={
            <Button as={Link} to="/account" $variant="ghost" $size="sm">계정 관리</Button>
          }
        />
        {loading ? (
          <Center>
            <Spinner $size={24} />
          </Center>
        ) : rankedAccounts.board.length === 0 ? (
          <EmptyState
            title="집계할 계정이 없습니다"
            description="Threads 계정을 연동하면 리더보드가 표시됩니다."
            action={
              <Button as={Link} to="/account" $size="sm">계정 연동</Button>
            }
          />
        ) : (
          <Leaderboard>
            {rankedAccounts.board.map((acc, i) => (
              <LeaderRow key={acc.threadAccountId}>
                <Rank>{i + 1}</Rank>
                <Avatar
                  src={acc.threadProfilePictureUrl}
                  name={acc.threadUserName}
                  size={38}
                />
                <LeaderMeta>
                  <LeaderName>
                    {acc.threadUserName
                      ? `@${acc.threadUserName}`
                      : `계정 #${acc.threadAccountId}`}
                  </LeaderName>
                  <LeaderSub>
                    발행 {acc.registeredCount} · 해제 {acc.unregisteredCount}
                  </LeaderSub>
                </LeaderMeta>
                <LeaderScore>{acc.totalCount}</LeaderScore>
              </LeaderRow>
            ))}
          </Leaderboard>
        )}
      </Card>
      <SectionGap />
      <Card><AiUsagePanel historyDays={range} refreshKey={insightRefresh} /></Card>
    </ReportPage>
  );
}

function Sparkline({
  values,
  color,
}: {
  values: number[];
  color: string;
}) {
  const theme = useTheme();
  if (values.length < 2 || values.every((v) => v === 0)) {
    return <SparkEmpty />;
  }

  const max = Math.max(...values);
  const min = Math.min(...values);
  const span = Math.max(1, max - min);
  const w = 140;
  const h = 40;
  const pad = 2;

  const coords = values.map((v, i) => {
    const x = (i / (values.length - 1)) * w;
    const y =
      pad + (h - pad * 2) - ((v - min) / span) * (h - pad * 2);
    return { x, y };
  });

  const line = coords.map((c) => `${c.x},${c.y}`).join(' ');
  const area = `0,${h} ${line} ${w},${h}`;

  return (
    <SparkSvg aria-hidden="true" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
      <defs>
        <linearGradient id={`spark-${color.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={0.28} />
          <stop offset="100%" stopColor={color} stopOpacity={0} />
        </linearGradient>
      </defs>
      <polygon
        points={area}
        fill={`url(#spark-${color.replace('#', '')})`}
      />
      <polyline
        points={line}
        fill="none"
        stroke={color}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle
        cx={coords[coords.length - 1].x}
        cy={coords[coords.length - 1].y}
        r={2.4}
        fill={theme.colors.surface}
        stroke={color}
        strokeWidth={1.6}
      />
    </SparkSvg>
  );
}

const ReportPage = styled.div`
  min-width: 0;
`;
const SectionHeading = styled.h2`
  margin-bottom: 8px;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: -0.02em;
`;
const ScopeNote = styled.p`
  font-size: 13px;
  line-height: 1.7;
  margin-bottom: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;
const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

const FilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 22px;
`;

const FilterPill = styled.label<{ $static?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 46px;
  padding: 8px 14px;
  border-radius: ${({ theme }) => theme.radius.pill};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  box-shadow: ${({ theme }) => theme.shadow.sm};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: ${({ $static }) => ($static ? 'default' : 'pointer')};

  strong {
    color: ${({ theme }) => theme.colors.textPrimary};
    font-weight: 700;
  }

  select {
    border: 0;
    background: transparent;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.textPrimary};
    cursor: pointer;
  }
`;

const AlertWrap = styled.div`
  margin-bottom: 18px;
`;

const SectionGap = styled.div`
  height: 24px;
`;

const MetricGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const MetricCard = styled.div`
  min-width: 0;
  padding: 22px;
  border-radius: ${({ theme }) => theme.radius.xl};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  box-shadow: ${({ theme }) => theme.shadow.sm};
  min-height: 128px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const MetricLabel = styled.p`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const MetricValue = styled.div`
  font-size: clamp(27px, 2.4vw, 34px);
  font-weight: 750;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.03em;
  line-height: 1.1;
  display: flex;
  align-items: baseline;
  gap: 6px;
`;

const MetricSlash = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const InsightGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const InsightCard = styled(MetricCard)`
  min-height: 118px;
`;

const InsightLabel = styled(MetricLabel)``;
const InsightValue = styled(MetricValue)`
  font-size: 28px;
`;

const SparkSvg = styled.svg`
  width: 100%;
  height: 40px;
  margin-top: auto;
`;

const SparkEmpty = styled.div`
  height: 40px;
  margin-top: auto;
`;

const SplitGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const TopicList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const TopicRow = styled.div`
  display: grid;
  grid-template-columns: 36px 1fr;
  gap: 12px;
  align-items: center;
`;

const TopicBody = styled.div`
  min-width: 0;
`;

const TopicTop = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
`;

const TopicName = styled.p`
  font-size: 13px;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TopicCount = styled.span`
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const ProgressTrack = styled.div`
  height: 8px;
  border-radius: ${({ theme }) => theme.radius.pill};
  background: ${({ theme }) => theme.colors.surfaceMuted};
  overflow: hidden;
`;

const ProgressFill = styled.div<{ $tone: 'strong' | 'weak' }>`
  height: 100%;
  border-radius: inherit;
  background: ${({ $tone, theme }) =>
    $tone === 'strong'
      ? `linear-gradient(90deg, ${theme.colors.success}, ${theme.colors.primary})`
      : `linear-gradient(90deg, ${theme.colors.warning}, ${theme.colors.danger})`};
  transition: width 0.35s ease;
`;

const Leaderboard = styled.div`
  display: flex;
  flex-direction: column;
`;

const LeaderRow = styled.div`
  display: grid;
  grid-template-columns: 28px 38px 1fr auto;
  gap: 12px;
  align-items: center;
  padding: 12px 4px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  &:last-child {
    border-bottom: none;
  }
`;

const Rank = styled.span`
  font-size: 13px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.textMuted};
  text-align: center;
`;

const LeaderMeta = styled.div`
  min-width: 0;
`;

const LeaderName = styled.p`
  font-size: 14px;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const LeaderSub = styled.p`
  margin-top: 2px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const LeaderScore = styled.span`
  font-size: 16px;
  font-weight: 750;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.02em;
`;

const Center = styled.div`
  display: flex;
  justify-content: center;
  padding: 36px;
  color: ${({ theme }) => theme.colors.primary};
`;
